import { supabase } from "@/integrations/supabase/client";
import { Invoice, InvoiceLine } from "@/lib/data";
import { getSubscriptionById } from "./subscriptionService";

interface InvoiceListOptions {
  page?: number;
  pageSize?: number;
}

export async function getInvoices(options: InvoiceListOptions = {}): Promise<{ data: Invoice[], count: number }> {
  const { page = 1, pageSize = 10 } = options;
  const start = (page - 1) * pageSize;
  
  // First get total count for pagination
  const { count, error: countError } = await supabase
    .from('invoices')
    .select('*', { count: 'exact', head: true });
  
  if (countError) {
    console.error('Error counting invoices:', countError);
    throw countError;
  }
  
  // Then fetch paginated data
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      invoice_id,
      subscription_id,
      status,
      invoice_date,
      due_date,
      total_amount
    `)
    .order('invoice_date', { ascending: false })
    .range(start, start + pageSize - 1);
  
  if (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }

  // Fetch minimal subscription details only for the invoices in current page
  const invoicesWithDetails = await Promise.all(
    data.map(async (invoice) => {
      const subscription = await getSubscriptionById(invoice.subscription_id, { minimal: true });
      
      return {
        invoice_id: invoice.invoice_id,
        subscription_id: invoice.subscription_id,
        subscription: subscription,
        status: invoice.status as Invoice['status'],
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        total_amount: invoice.total_amount,
        // Don't fetch invoice lines here
      };
    })
  );
  
  return { 
    data: invoicesWithDetails, 
    count: count || 0 
  };
}

// Update subscriptionService to add a minimal flag
export async function getInvoiceById(id: string, options: { minimal?: boolean } = {}): Promise<Invoice | null> {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('invoice_id', id)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching invoice:', error);
    throw error;
  }

  // For minimal requests, we don't need to load invoice lines
  let invoiceLines: InvoiceLine[] = [];
  if (!options.minimal) {
    invoiceLines = await getInvoiceLines(data.invoice_id);
  }

  const subscription = await getSubscriptionById(data.subscription_id);
  
  return {
    invoice_id: data.invoice_id,
    subscription_id: data.subscription_id,
    subscription: subscription!,
    status: data.status as Invoice['status'],
    invoice_date: data.invoice_date,
    due_date: data.due_date,
    total_amount: data.total_amount,
    lines: invoiceLines
  };
}

// Keep other functions the same
export async function getInvoicesBySubscriptionId(subscriptionId: string): Promise<Invoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('subscription_id', subscriptionId);
  
  if (error) {
    console.error('Error fetching invoices by subscription:', error);
    throw error;
  }

  const subscription = await getSubscriptionById(subscriptionId);
  
  const invoicesWithDetails = await Promise.all(
    data.map(async (invoice) => {
      const invoiceLines = await getInvoiceLines(invoice.invoice_id);
      
      return {
        invoice_id: invoice.invoice_id,
        subscription_id: invoice.subscription_id,
        subscription: subscription!,
        status: invoice.status as Invoice['status'],
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        total_amount: invoice.total_amount,
        lines: invoiceLines
      };
    })
  );
  
  return invoicesWithDetails;
}

export async function getInvoiceLines(invoiceId: string): Promise<InvoiceLine[]> {
  const { data, error } = await supabase
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', invoiceId);
  
  if (error) {
    console.error('Error fetching invoice lines:', error);
    throw error;
  }
  
  return data.map(line => ({
    line_id: line.line_id,
    invoice_id: line.invoice_id,
    service_name: line.service_name,
    description: line.description,
    period_start: line.period_start,
    period_end: line.period_end,
    quantity: line.quantity,
    unit_price: line.unit_price,
    total_price: line.total_price
  }));
}
